import { useState } from 'react';
import { useStore } from '../store';
import {
  STREAM_QUALITIES,
  canShareScreen,
  lastGoLiveOptions,
  startScreenShare,
  stopScreenShare,
  useVoice,
  type GoLiveOptions,
} from '../lib/voice';
import { Button, Modal, cx } from './ui';
import { CallButton } from './VoiceUi';

/** Screen share button for call controls: opens the Go Live dialog, or stops sharing. */
export function ScreenShareButton() {
  const screen = useVoice((s) => s.screen);
  const [open, setOpen] = useState(false);
  if (!canShareScreen())
    return (
      <span title="This browser can't share its screen" className="opacity-40">
        <CallButton
          title="Screen sharing isn't available here"
          icon="monitor"
          onClick={async () => {}}
        />
      </span>
    );
  return (
    <>
      <CallButton
        title={screen ? 'Stop sharing' : 'Share your screen'}
        on={screen}
        icon="monitor"
        onClick={async () => (screen ? stopScreenShare() : setOpen(true))}
      />
      {open && <GoLiveDialog onClose={() => setOpen(false)} />}
    </>
  );
}

/** Pick quality, what you're sharing, and audio, then pick the screen in the browser's dialog. */
export function GoLiveDialog({ onClose }: { onClose: () => void }) {
  const max = useStore((s) => s.maxStreamQuality);
  const live = useVoice((s) => s.screen);
  const [opts, setOpts] = useState<GoLiveOptions>(() => lastGoLiveOptions(max));
  const allowed = STREAM_QUALITIES.slice(0, STREAM_QUALITIES.findIndex((q) => q.id === max) + 1);
  const option = (selected: boolean) =>
    cx(
      'flex-1 rounded border px-3 py-2 text-left text-sm',
      selected
        ? 'border-accent bg-accent/15 text-text-0'
        : 'border-bg-3 text-text-2 hover:bg-bg-hover',
    );
  return (
    <Modal title={live ? 'Change stream quality' : 'Go Live'} onClose={onClose}>
      <div className="space-y-5">
        <fieldset>
          <legend className="mb-2 text-xs font-bold uppercase text-text-3">Quality</legend>
          <div className="flex gap-2">
            {allowed.map((q) => (
              <button
                key={q.id}
                type="button"
                aria-pressed={opts.quality === q.id}
                className={option(opts.quality === q.id)}
                onClick={() => setOpts({ ...opts, quality: q.id })}
              >
                {q.label}
              </button>
            ))}
          </div>
        </fieldset>
        <fieldset>
          <legend className="mb-2 text-xs font-bold uppercase text-text-3">Optimize for</legend>
          <div className="flex gap-2">
            <button
              type="button"
              aria-pressed={opts.content === 'detail'}
              className={option(opts.content === 'detail')}
              onClick={() => setOpts({ ...opts, content: 'detail' })}
            >
              <div className="font-medium">Text &amp; detail</div>
              <div className="text-xs text-text-3">Code, documents, slides</div>
            </button>
            <button
              type="button"
              aria-pressed={opts.content === 'motion'}
              className={option(opts.content === 'motion')}
              onClick={() => setOpts({ ...opts, content: 'motion' })}
            >
              <div className="font-medium">Motion</div>
              <div className="text-xs text-text-3">Games, videos</div>
            </button>
          </div>
        </fieldset>
        <label className="flex items-center gap-2 text-sm text-text-1">
          <input
            type="checkbox"
            checked={opts.audio}
            onChange={(e) => setOpts({ ...opts, audio: e.target.checked })}
          />
          Share audio (when your browser allows it for that screen, window or tab)
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              onClose();
              void startScreenShare(opts);
            }}
          >
            {live ? 'Restart stream' : 'Go Live'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
